import { BehaviorSubject, defer, Observable } from 'rxjs';
import { renderPdfPages, RenderPdfPagesOptions } from './pdf-pages';

/**
 * Adds the rendered pages of every PDF in an upload form, so the server files them instead of
 * converting the PDF itself (server side: `utils/pdfPages.js`).
 *
 * Each PDF's pages go in parts named `pdfPage[<n>]`, n being the PDF's position among the form's
 * PDFs, appended after everything else in page order. A PDF that cannot be rendered, or stops
 * finishing pages, is sent as it is and the server converts it, so a rendering problem never stops an
 * upload. Progress goes to `pdfPagesProgress$`, which the app's loader shows.
 */

export const pageFieldName = (pdfIndex: number) => `pdfPage[${pdfIndex}]`;

export interface WithPdfPagesOptions {
  /** 150 suits viewing and the AI read of notes; document returns use 200. */
  dpi?: number;
  /** How long a PDF may go without finishing a page before it is sent without pages. */
  stallMs?: number;
  render?: (file: Blob, options: RenderPdfPagesOptions & { dpi: number }) => Promise<Blob[]>;
}

export interface PdfPagesProgress {
  done: number;
  total: number;
}

const progress = new BehaviorSubject<PdfPagesProgress | null>(null);
/** Pages done and page count across every upload preparing PDF pages; null when none is. */
export const pdfPagesProgress$ = progress.asObservable();

const running = new Map<symbol, PdfPagesProgress>();
function publish(): void {
  if (!running.size) {
    progress.next(null);
    return;
  }
  let done = 0;
  let total = 0;
  running.forEach((job) => {
    done += job.done;
    total += job.total;
  });
  progress.next({ done, total });
}

export function pdfProgressText(state: PdfPagesProgress | null): string {
  if (!state) return '';
  return state.total ? `Preparing PDF pages ${state.done} of ${state.total}…` : 'Preparing PDF…';
}

/**
 * Pages rendered in the background as soon as a PDF is added to an upload container, kept in memory (never in
 * the container) until the form is saved. Matched by the file's contents, because the services rebuild the file
 * from the container's object URL. A few at most: a PDF added and then removed is dropped in time.
 */
interface PreparedPages {
  pages: Promise<Blob[]>;
  done: number;
  total: number;
  onPage?: (done: number, total: number) => void;
}

const PREPARED_LIMIT = 5;
const prepared = new Map<string, PreparedPages>();

/** SHA-256 of the file and the resolution; null where the browser cannot hash (then nothing is shared). */
async function preparedKey(file: Blob, dpi: number): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null;
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex}:${dpi}`;
}

/** Starts rendering `file`'s pages in the background for a later save. Resolves once started. */
export async function preparePdfPages(file: Blob, options: Pick<WithPdfPagesOptions, 'dpi' | 'render'> = {}): Promise<void> {
  const { dpi = 150, render = renderPdfPages } = options;
  const key = await preparedKey(file, dpi);
  if (!key || prepared.has(key)) return;
  const entry: PreparedPages = { pages: Promise.resolve([]), done: 0, total: 0 };
  entry.pages = render(file, {
    dpi,
    onPage: (done, total) => {
      entry.done = done;
      entry.total = total;
      entry.onPage?.(done, total);
    },
  });
  entry.pages.catch(() => {
    if (prepared.get(key) === entry) prepared.delete(key);
  });
  prepared.set(key, entry);
  while (prepared.size > PREPARED_LIMIT) prepared.delete(prepared.keys().next().value as string);
}

/** The background pages for `file`, taken out of the store, or null when there are none. */
async function takePrepared(file: Blob, dpi: number): Promise<PreparedPages | null> {
  const key = await preparedKey(file, dpi);
  const entry = key ? prepared.get(key) : undefined;
  if (!key || !entry) return null;
  prepared.delete(key);
  return entry;
}

export async function withPdfPages(form: FormData, options: WithPdfPagesOptions = {}): Promise<FormData> {
  const { dpi = 150, stallMs = 60_000, render = renderPdfPages } = options;

  const pdfs: File[] = [];
  form.forEach((value) => {
    if (value instanceof File && value.type === 'application/pdf') pdfs.push(value);
  });
  if (!pdfs.length) return form;

  // Each PDF counts its own pages until the whole form is done; one not opened yet counts as nothing.
  const jobs = pdfs.map(() => ({ id: Symbol('pdf'), done: 0, total: 0 }));
  jobs.forEach((job) => running.set(job.id, job));
  publish();
  try {
    for (const [index, pdf] of pdfs.entries()) {
      const job = jobs[index];
      const progress = (alive: () => void) => (done: number, total: number) => {
        job.done = done;
        job.total = total;
        publish();
        alive();
      };
      let pages: Blob[] | null = null;
      const ready = await takePrepared(pdf, dpi);
      if (ready) {
        // Already rendering since the PDF was added: wait for it, showing its progress.
        job.done = ready.done;
        job.total = ready.total;
        publish();
        try {
          pages = await untilStalled(stallMs, (alive) => {
            ready.onPage = progress(alive);
            return ready.pages;
          });
        } catch {
          pages = null;
        }
      }
      if (!pages) {
        try {
          pages = await untilStalled(stallMs, (alive) => render(pdf, { dpi, onPage: progress(alive) }));
        } catch {
          continue;
        }
      }
      const stem = pdf.name.replace(/\.[^.]+$/, '') || 'document';
      pages.forEach((page, n) => form.append(pageFieldName(index), page, `${stem}-p${n + 1}.jpg`));
    }
  } finally {
    jobs.forEach((job) => running.delete(job.id));
    publish();
  }
  return form;
}

/**
 * For a service method: the request body with its PDFs' pages added when it is a FormData, any
 * other body as it is. Nothing is rendered until the request is subscribed to, as with HttpClient.
 */
export function withPdfPages$<T>(body: T, options: WithPdfPagesOptions = {}): Observable<T> {
  return defer(() => (body instanceof FormData ? withPdfPages(body, options) : Promise.resolve(body)) as Promise<T>);
}

/** Rejects when `work` goes `ms` without calling `alive`; each call restarts the clock. */
function untilStalled<T>(ms: number, work: (alive: () => void) => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let timer = setTimeout(() => reject(new Error('stalled')), ms);
    const alive = () => {
      clearTimeout(timer);
      timer = setTimeout(() => reject(new Error('stalled')), ms);
    };
    work(alive).then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => { clearTimeout(timer); reject(error); },
    );
  });
}
